<template lang="pug">
.enum-select
  label.control-label
    .d-flex.align-items-center
      | {{ label }}
      i.hi.hi-info-filled.hi-12.a-tooltip.mx-5(
        v-tooltip
        :title="labelTooltip"
      )
  .d-flex.flex-column.flex-sm-row.gap-20
    AppSelect.idle-timeout(
      :value="idleTimeout"
      :options="selectOptions"
      :disabled="disabled"
      @input="onChange"
    )
    .custom-input(v-if="customSelected")
      ValidationProvider(
        v-slot="{ errors }"
        :rules="rules"
      )
        .d-flex.align-items-center.flex-wrap(:class="{ 'has-error': errors[0] }")
          input.form-control(
            v-model="customIdleTimeout"
            type="number"
            :min="1"
            :max="60"
            :step="1"
            @input="onInput"
          )
          span.input-group-addon Min
        .has-error(v-if="errors.length > 0")
          span.help-block.mb-0
            | Enter a number between 1 - 60
</template>

<script>
import { extend } from '~/utils/vee_validate_compat';
import { numeric, required } from '@vee-validate/rules';
import VModelCompat from '~/mixins/v_model_compat';

extend('numeric', numeric);
extend('required', required);

export default {
  name: 'IdleTimeoutSelect',
  mixins: [VModelCompat],
  props: {
    label: { type: String, required: true },
    labelTooltip: { type: String, required: true },
    disabledTooltip: { type: String, required: true },
    options: { type: Array, required: true },
    modelValue: { type: [Number, String], default: undefined },
    value: { type: [Number, String], default: undefined },
    customDisabled: { type: Boolean, default: false },
    customIsSelected: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },

  data() {
    return {
      customIdleTimeout: null, // in minutes
      customSelected: false,
      customId: 'undefined',
    };
  },

  computed: {
    selectOptions() {
      return this.processSelectOptions(this.options);
    },
    rules() {
      return {
        between: { min: 1, max: 60 },
        required: true,
        numeric: true
      };
    },
    // value for AppSelect (:value)
    idleTimeout() {
      // if we are in Custom, force select to show Custom
      if (this.customSelected) {
        return String(this.customId);
      }

      // else, if value matches pre defined, return the number
      const matchesPre = this.options.some(
        (o) => o.text !== 'Custom' && Number(o.id) === Number(this.modelCompatValue)
      );

      if (matchesPre) {
        return Number(this.modelCompatValue);
      }

      // fallback: show custom
      return String(this.customId);
    },
  },

  watch: {
    modelCompatValue: {
      immediate: true,
      handler(newVal) {
        this.syncFromValue(newVal);
      },
    },
    options: {
      immediate: true,
      handler() {
        const opt = this.options.find((o) => o.text === 'Custom');

        this.customId = opt ? opt.id : 'undefined';
        this.syncFromValue(this.modelCompatValue);
      },
    },
  },

  mounted() {
    const opt = this.options.find((o) => o.text === 'Custom');

    this.customId = opt ? opt.id : 'undefined';
    // if parent already declared customIsSelected, keep it
    if (this.customIsSelected) {
      this.customSelected = true;
    }

    this.syncFromValue(this.modelCompatValue);
  },

  methods: {
    isPredefined(val) {
      return this.options.some((o) => o.text !== 'Custom' && Number(o.id) === Number(val));
    },

    syncFromValue(val) {
      // sync the state with parent value
      if (this.isPredefined(val)) {
        this.customSelected = false;
      } else {
        this.customSelected = true;
        if (!Number.isNaN(Number(val)) && String(val) !== String(this.customId)) {
          this.customIdleTimeout = Number(val) / 60;
        } else if (!this.customIdleTimeout) {
          this.customIdleTimeout = 15; // friendly default
        }
      }
    },

    onInput() {
      // user changed custom field in minutes
      const v = Number(this.customIdleTimeout);

      if (Number.isNaN(v)) {
        return;
      }

      const clamped = Math.max(1, Math.min(60, Math.floor(v)));

      this.customIdleTimeout = clamped;
      const seconds = clamped * 60;
      // if the number is in selection (pre defined), goes out of custom
      const matchesPre = this.options.some((o) => Number(o.id) === seconds);

      this.customSelected = !matchesPre;
      this.emitModelCompatValue(seconds);
    },

    onChange(value) {
      // deals with changes coming from select
      const customId = String(this.customId);
      const predefinedIds = this.options
        .filter((o) => o.text !== 'Custom')
        .map((o) => String(o.id));
      const valStr = String(value);

      if (valStr === customId) {
        // user selected Custom -> shows input
        this.customSelected = true;
        if (!this.customIdleTimeout) {
          this.customIdleTimeout = 15;
        }

        this.emitModelCompatValue(Number(this.customIdleTimeout) * 60);

        return;
      }

      if (predefinedIds.includes(valStr)) {
        this.customSelected = false;
        this.emitModelCompatValue(Number(value));

        return;
      }

      // fallback: if unexpected number: deal as custom
      const num = Number(value);

      if (!Number.isNaN(num)) {
        const matches = this.options.some((o) => Number(o.id) === num);

        this.customSelected = !matches;
        if (this.customSelected) {
          this.customIdleTimeout = num / 60;
        }

        this.emitModelCompatValue(num);

        return;
      }

      this.emitModelCompatValue(value);
    },
    processSelectOptions(options) {
      const option = options.find((o) => o.text === 'Custom');

      if (!option) {return options;}

      if (this.customDisabled) {
        option.disabled = true;
        option.disabledHint = this.disabledTooltip;
        option.tooltipOptions = {
          placement: 'right auto',
          template:
            '<div class="tooltip disabled-option-tooltip" role="tooltip">' +
            '<div class="tooltip-arrow disabled-option-tooltip-arrow"></div>' +
            '<div class="tooltip-inner disabled-option-tooltip-inner"></div>' +
            '</div>',
        };
      }
      // keep the Custom id the same as expected by the application
      option.id = this.customId ?? option.id;

      return options;
    },
  },
};
</script>
